import { FaceTecURScanMatchInitializationError, FaceTecURScanMatchSDKInstance, FaceTecURScanMatchSessionStatus } from "../../core-sdk/FaceTecURScanMatchSDK.js/FaceTecURScanMatchPublicAPI";
import { URCodesSampleUtilities } from "./helpers/URCodesSampleUtilities";
import { Config } from "./Config";

export class URCodesSampleController {
  protected faceTecURScanMatchSDKInstance!: FaceTecURScanMatchSDKInstance;

  public constructor() {
    console.log("[FaceTec Debug] URCodesSampleController constructor called");

    const sdk = (window as any).FaceTecURScanMatchSDK;
    if (typeof sdk === 'undefined') {
      console.error("[FaceTec Debug] FaceTecURScanMatchSDK is not available on window!");
      throw new Error("FaceTecURScanMatchSDK is not loaded. Please ensure the SDK script loads before the controller.");
    }

    console.log("[FaceTec Debug] FaceTecURScanMatchSDK is available on window, setting up...");

    URCodesSampleUtilities.setupBrowserInterfaceBasedOnDevice();

    console.log("[FaceTec Debug] Setting images directory...");
    sdk.setImagesDirectory("/ur/core-sdk/URCode_images");

    console.log("[FaceTec Debug] Setting resource directory...");
    sdk.setResourceDirectory("/ur/core-sdk/FaceTecURScanMatchSDK.js/resources");

    // Configure custom URLs and public keys for local development
    // This prevents CORS issues when running locally
    const publicKeyData = {
      domain: "envoc.com/ur/",
      publicKeys: [
        {
          publicKey: "-----BEGIN PUBLIC KEY-----\nMFYwEAYHKoZIzj0CAQYFK4EEAAoDQgAERhp7Cb7yyMasZlwpb9yxMV7yKvX4BW2Q\nLFyX2t5stcB42hjuV3usNL5ak5i+nbkdb9Il0QelL8kD0GAuQAUurw==\n-----END PUBLIC KEY-----",
          sig: "MEUCIQC7ZywEdVcJDjNT/N1fPTQQJva7gDq8rqB43Uzmyl3GWAIgKdXhhgFl1qlSGVcX2f/2OvX7AAIA2AHYFTq/DJRKz/0="
        }
      ]
    };
    console.log("[FaceTec Debug] Setting custom URLs and public keys...");
    sdk.setCustomURLsAndPublicKeys([publicKeyData]);

    console.log("[FaceTec Debug] URCodesSampleController constructor completed");
  }

  // Initialize the FaceTec UR Scan + Match SDK.
  // Required Parameters:
  // - licenseText (string): The UR Scan + Match SDK License associated with Your Application.
  // - callback (FaceTecURScanMatchInitializeCallback):
  //      - The onSuccess callback is called with a FaceTecURScanMatchSDKInstance when successful.
  //      - The onError callback is called when the Device is not compatible, the license cannot be validated due to network issues,
  //        an invalid license is used, or an unknown error occurs.
  public initializeURScanMatchSDK = (): void => {
    // Setup the session start button
    document.getElementById("ur-code-scan-plus-match-button")!.onclick = (): void => {
      this.startURScanMatchSession();
    };
    URCodesSampleUtilities.enableAllButtons();

    const sdk = (window as any).FaceTecURScanMatchSDK;
    sdk.initialize(Config.LicenseText, {
      onSuccess: (newFaceTecURScanMatchSdkInstance: FaceTecURScanMatchSDKInstance) => {
        this.faceTecURScanMatchSDKInstance = newFaceTecURScanMatchSdkInstance;

        URCodesSampleUtilities.demonstrateHandlingInitializationSuccess();
      },
      onError: (facetecScanMatchInitializationError: FaceTecURScanMatchInitializationError): void => {
        URCodesSampleUtilities.demonstrateHandlingInitializationError(facetecScanMatchInitializationError);
      }
    });
  };

  // Start a session with the FaceTec UR Scan Match SDK.
  public startURScanMatchSession = (): void => {
    URCodesSampleUtilities.fadeOutMainUIAndPrepareForSession();

    this.faceTecURScanMatchSDKInstance.startURCodeMatch({
      onComplete: (sessionStatus: FaceTecURScanMatchSessionStatus) => {
        URCodesSampleUtilities.demonstrateHandlingSessionComplete(sessionStatus);
      }
    });
  };
}

function waitForSDK(callback: () => void, maxWait: number = 10000) {
  const startTime = Date.now();

  function checkSDK() {
    const windowSdk = typeof window !== 'undefined' && (window as any).FaceTecURScanMatchSDK;

    console.log("[FaceTec Debug] Checking SDK availability on window:", !!windowSdk);

    if (windowSdk) {
      console.log("[FaceTec Debug] SDK is ready on window, initializing controller...");
      callback();
      return;
    }

    if (Date.now() - startTime > maxWait) {
      console.error("[FaceTec Debug] SDK failed to load within timeout, initializing anyway...");
      console.log("[FaceTec Debug] Final check - window:", typeof (window as any).FaceTecURScanMatchSDK);
      callback();
      return;
    }

    console.log("[FaceTec Debug] Waiting for SDK to load...");
    setTimeout(checkSDK, 100);
  }

  checkSDK();
}

window.onload = (): void => {
  console.log("[FaceTec Debug] Window onload fired, waiting for SDK...");
  waitForSDK(() => {
    new URCodesSampleController().initializeURScanMatchSDK();
  });
};
